import { useParams, useLocation, Link } from "react-router-dom";
import { CheckCircle2, ExternalLink, Sparkles } from "lucide-react";
import { PAGES } from "@/data/site";
import { EXTRA, PAGE_KEYWORDS } from "@/data/seoExtra";
import { MORE, MORE_KEYWORDS } from "@/data/seoMore";
import { useSeo, breadcrumbLd, faqLd } from "@/lib/seo";
import { PageHero, CtaBand, Faq, RelatedLinks, SisterLinks, VisualNote } from "@/components/public/Blocks";
import NotFound from "@/pages/public/NotFound";
import { BlogTeaser } from "@/pages/public/Blog";

const BLOG_CAT = { "/traitement-termites": "Termites", "/traitement-capricorne": "Capricorne", "/traitement-vrillette": "Vrillette", "/traitement-moustiques": "Moustiques", "/traitement-humidite": "Humidité", "/certibiocide": "Réglementation" };

function Section({ s, i }) {
  return (
    <div className={`grid items-center gap-8 py-10 ${s.image ? "lg:grid-cols-2" : ""}`}>
      <div className={i % 2 && s.image ? "lg:order-2" : ""}>
        <span className="font-display text-sm font-black text-[#C06014]">0{i + 1}</span>
        <h2 className="mt-1 text-2xl font-black tracking-tight md:text-3xl">{s.h2}</h2>
        {s.text && <p className="mt-3 text-base leading-relaxed text-muted-foreground">{s.text}</p>}
        {s.bullets && (
          <ul className="mt-4 grid gap-2 sm:grid-cols-2">
            {s.bullets.map((b) => (
              <li key={b} className="flex items-start gap-2 rounded-lg bg-white p-3 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#53634F]" />{b}</li>
            ))}
          </ul>
        )}
        {s.link && (s.link.href.startsWith("/") ? (
          <Link to={s.link.href} className="mt-4 inline-flex items-center gap-1.5 font-semibold text-[#C06014] underline-offset-4 hover:underline" data-testid="section-internal-link">{s.link.label} →</Link>
        ) : (
          <a href={s.link.href} target="_blank" rel="noopener" className="mt-4 inline-flex items-center gap-1.5 font-semibold text-[#C06014] underline-offset-4 hover:underline" data-testid="section-external-link">
            {s.link.label} <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ))}
      </div>
      {s.image && <img src={s.image} alt={s.h2} loading="lazy" className="aspect-[4/3] w-full rounded-2xl object-cover shadow-lg" />}
    </div>
  );
}

export default function InfoPage() {
  const { pathname } = useLocation();
  const base = PAGES[pathname];
  const extra = EXTRA[pathname] || {};
  const more = MORE[pathname] || {};
  const page = base && { ...base, sections: [...base.sections, ...(extra.sections || []), ...(more.sections || [])], faq: [...base.faq, ...(extra.faq || []), ...(more.faq || [])], summary: extra.summary };
  useParams();
  const crumbs = [{ name: "Accueil", path: "/" }, { name: page?.h1 || "", path: pathname }];
  useSeo({
    title: page?.title || "Page introuvable | Termite Habitat",
    description: page?.description,
    path: pathname,
    image: page?.image,
    noindex: !page,
    jsonLd: page && {
      "@context": "https://schema.org",
      "@graph": [
        breadcrumbLd(crumbs),
        faqLd(page.faq),
        { "@type": "WebPage", name: page.title, description: page.description, inLanguage: "fr-FR", dateModified: "2026-10-26", keywords: [...(PAGE_KEYWORDS[pathname] || []), ...(MORE_KEYWORDS[pathname] || [])].join(", "), speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "[data-testid='page-summary']"] }, publisher: { "@id": "https://www.termitehabitat.fr/#org" } },
        { "@type": "Service", name: page.h1, serviceType: page.h1, description: page.description, provider: { "@id": "https://www.termitehabitat.fr/#business" }, areaServed: ["Gironde", "Bordeaux Métropole", "Médoc", "Bassin d'Arcachon", "Libournais", "Nouvelle-Aquitaine"], ...(page.summary ? { disambiguatingDescription: page.summary.join(". ") } : {}) },
      ],
    },
  });
  if (!page) return <NotFound />;
  return (
    <>
      <PageHero kicker={page.kicker} h1={page.h1} intro={page.intro} image={page.image} crumbs={crumbs} />
      {page.summary && (
        <section className="container-x -mt-8 relative z-10">
          <div className="rounded-2xl border bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="page-summary">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#C06014]"><Sparkles className="h-4 w-4" /> L'essentiel en 30 secondes</p>
            <ul className="mt-3 grid gap-2 md:grid-cols-2">{page.summary.map((s) => <li key={s} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#53634F]" />{s}</li>)}</ul>
          </div>
        </section>
      )}
      <article className="container-x divide-y divide-black/10 py-6">
        {page.sections.map((s, i) => <Section key={s.h2} s={s} i={i} />)}
      </article>
      <div className="container-x"><VisualNote /></div>
      <Faq faq={page.faq} />
      <CtaBand {...page.cta} />
      <RelatedLinks paths={page.related} />
      <BlogTeaser category={BLOG_CAT[pathname]} title={BLOG_CAT[pathname] ? `Nos articles : ${BLOG_CAT[pathname].toLowerCase()}` : undefined} />
      <SisterLinks />
    </>
  );
}
